// SVG-based panel board renderer.
// Devices are drawn on the rails of a chosen enclosure (cuadro) background image,
// in the enclosure's own viewBox (millimetre) coordinate space.
// Read-only when interaction props are absent; interactive (click-to-move + drag) when provided.

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolvePanelSymbol } from './symbols/registry';
import type { EnclosureGeometry } from './enclosures/registry';
import type { PanelDevice, PanelLayout } from './types';

// Pointer movement (in viewBox units) required before switching from click to drag mode.
const DRAG_THRESHOLD = 4;

// On-screen px per viewBox (millimetre) unit. The viewBox stays in mm so device
// coordinates line up with the enclosure asset; this only scales the rendered size.
const DISPLAY_SCALE = 2.4;

// Short technical labels per device type (not i18n: these are standard abbreviations).
const TYPE_SHORT: Record<string, string> = {
  incoming_breaker: 'IGA',
  mcb:              'MCB',
  rcd:              'RCD',
  rcbo:             'RCBO',
  spd:              'SPD',
  n_busbar:         'N',
  pe_busbar:        'PE',
  outgoing_line:    'Out',
  reserve_module:   '—',
};

// CSS-variable-based colors per device type.
type Colors = { fill: string; stroke: string; text: string };

const TYPE_COLORS: Record<string, Colors> = {
  incoming_breaker: {
    fill:   'rgb(var(--accent) / 0.16)',
    stroke: 'rgb(var(--accent) / 0.65)',
    text:   'rgb(var(--accent))',
  },
  rcd: {
    fill:   'rgb(var(--warning) / 0.14)',
    stroke: 'rgb(var(--warning) / 0.55)',
    text:   'rgb(var(--warning))',
  },
  rcbo: {
    fill:   'rgb(var(--warning) / 0.14)',
    stroke: 'rgb(var(--warning) / 0.55)',
    text:   'rgb(var(--warning))',
  },
  spd: {
    fill:   'rgb(var(--success) / 0.14)',
    stroke: 'rgb(var(--success) / 0.55)',
    text:   'rgb(var(--success))',
  },
};

const DEFAULT_COLORS: Colors = {
  fill:   'rgb(var(--surface-3))',
  stroke: 'rgb(var(--border))',
  text:   'rgb(var(--ink))',
};

function colorsFor(deviceType: string): Colors {
  return TYPE_COLORS[deviceType] ?? DEFAULT_COLORS;
}

// Scan left within a row to find the anchor deviceId for a device_continuation slot.
function findContinuationAnchor(layout: PanelLayout, row: number, col: number): string | null {
  const panelRow = layout.rows.find(r => r.rowIndex === row);
  if (!panelRow) return null;
  for (let c = col - 1; c >= 0; c--) {
    const s = panelRow.slots.find(s => s.columnIndex === c);
    if (!s) break;
    if (s.kind === 'device_anchor' && s.device) return s.device.deviceId;
    if (s.kind !== 'device_continuation') break;
  }
  return null;
}

// Convert client pointer coordinates to a grid {row, col} via the enclosure geometry.
function pointToGridSlot(
  clientX: number,
  clientY: number,
  svgEl: SVGSVGElement,
  geometry: EnclosureGeometry,
): { row: number; col: number } | null {
  const rect = svgEl.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;
  const svgX = (clientX - rect.left) * (geometry.viewW / rect.width);
  const svgY = (clientY - rect.top) * (geometry.viewH / rect.height);
  return geometry.pointToSlot(svgX, svgY);
}

// ─── Device rectangle ────────────────────────────────────────────────────────

function DeviceRect({
  device,
  lineLabel,
  x,
  y,
  w,
  h,
  isSelected,
  isHighlighted,
  isDragging,
  isSwapCandidate,
  isSwapTarget,
  onPointerDown,
}: {
  device: PanelDevice;
  lineLabel?: string;
  x: number;
  y: number;
  w: number;
  h: number;
  isSelected?: boolean;
  isHighlighted?: boolean;
  isDragging?: boolean;
  // Swap feedback while another device is being dragged: candidate = a valid swap
  // partner; target = the one currently under the drag cursor.
  isSwapCandidate?: boolean;
  isSwapTarget?: boolean;
  onPointerDown?: (e: React.PointerEvent) => void;
}) {
  const cx = x + w / 2;
  const clr = colorsFor(device.deviceType);
  const typeLabel = TYPE_SHORT[device.deviceType] ?? device.deviceType.slice(0, 5).toUpperCase();
  const svgUrl = resolvePanelSymbol(device.symbolId, device.ratingA || undefined);

  const tooltipText = [
    device.deviceType,
    device.ratingA > 0 ? `${device.ratingA}A` : null,
    device.poleCount > 0 ? `${device.poleCount}P` : null,
    `${device.modulesWidth}M`,
    device.symbolId,
  ].filter(Boolean).join(' · ');

  return (
    <g
      onPointerDown={onPointerDown}
      style={{
        cursor: onPointerDown ? (isDragging ? 'grabbing' : 'grab') : undefined,
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      <title>{tooltipText}</title>
      {svgUrl ? (
        <>
          {/* SVG symbol fills the module footprint; SVGs carry their own white background */}
          <image
            href={svgUrl}
            x={x} y={y} width={w} height={h}
            preserveAspectRatio="none"
          />
          {/* Permanent subtle border — print-ready, no selection styling */}
          <rect
            x={x} y={y} width={w} height={h} rx={2}
            style={{ fill: 'none', stroke: clr.stroke, strokeWidth: 0.8 }}
          />
          {/* Selection highlight — hidden in print; inset so the stroke stays inside the contour. */}
          {isSelected && (
            <rect
              className="no-print"
              x={x + 1} y={y + 1} width={w - 2} height={h - 2} rx={1.5}
              style={{
                fill: 'rgb(var(--accent) / 0.10)',
                stroke: 'rgb(var(--accent))',
                strokeWidth: 1.6,
              }}
            />
          )}
        </>
      ) : (
        <>
          {/* Rectangle fallback — print-ready (type color, no selection styling) */}
          <rect
            x={x} y={y} width={w} height={h} rx={2}
            style={{ fill: clr.fill, stroke: clr.stroke, strokeWidth: 1 }}
          />
          {/* Selection highlight — hidden in print; inset so the stroke stays inside the contour. */}
          {isSelected && (
            <rect
              className="no-print"
              x={x + 1} y={y + 1} width={w - 2} height={h - 2} rx={1.5}
              style={{
                fill: 'none',
                stroke: 'rgb(var(--accent))',
                strokeWidth: 1.6,
              }}
            />
          )}

          <text
            x={cx} y={y + 0.32 * h}
            textAnchor="middle"
            style={{ fill: clr.text, fontSize: '9px', fontWeight: '700', fontFamily: 'inherit' }}
          >
            {typeLabel}
          </text>

          {device.ratingA > 0 && (
            <text
              x={cx} y={y + 0.5 * h}
              textAnchor="middle"
              style={{ fill: clr.text, fontSize: '8px', fontFamily: 'inherit' }}
            >
              {device.ratingA}A
            </text>
          )}

          <text
            x={cx} y={y + 0.66 * h}
            textAnchor="middle"
            style={{ fill: 'rgb(var(--ink-4))', fontSize: '7px', fontFamily: 'inherit' }}
          >
            {device.modulesWidth}M
          </text>
        </>
      )}

      {/* Relationship highlight — accent tint covering the whole module, drawn on
          top so it reads over both SVG symbols and rectangle fallbacks. Hidden in
          print: the highlight is interaction feedback, not part of the layout. */}
      {isHighlighted && (
        <rect
          className="no-print"
          x={x} y={y} width={w} height={h} rx={2}
          style={{
            fill: 'rgb(var(--accent) / 0.16)',
            stroke: 'rgb(var(--accent) / 0.55)',
            strokeWidth: 1,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Swap feedback (drag only, screen-only): green outline on every valid swap
          partner, filled + stronger on the one under the cursor — mirrors the green
          used for valid empty-slot drop targets. */}
      {(isSwapCandidate || isSwapTarget) && (
        <rect
          className="no-print"
          x={x + 0.5} y={y} width={w - 1} height={h} rx={1.5}
          style={{
            fill: isSwapTarget ? 'rgb(var(--success) / 0.30)' : 'none',
            stroke: 'rgb(var(--success))',
            strokeWidth: isSwapTarget ? 1.6 : 1,
            strokeDasharray: isSwapTarget ? undefined : '3 2',
            pointerEvents: 'none',
          }}
        />
      )}

      {lineLabel && (
        <g pointerEvents="none">
          <rect
            x={x + 2}
            y={y + h - 13}
            width={Math.max(12, w - 4)}
            height={10}
            rx={1.5}
            style={{
              fill: 'rgb(var(--surface) / 0.88)',
              stroke: 'rgb(var(--border) / 0.75)',
              strokeWidth: 0.5,
            }}
          />
          <text
            x={cx}
            y={y + h - 5.2}
            textAnchor="middle"
            style={{
              fill: 'rgb(var(--ink))',
              fontSize: '6.5px',
              fontWeight: 700,
              fontFamily: 'inherit',
            }}
          >
            {lineLabel}
          </text>
        </g>
      )}
    </g>
  );
}

// ─── Reserve slot ─────────────────────────────────────────────────────────────

function ReserveRect({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return (
    <rect
      x={x + 0.5} y={y} width={w - 1} height={h} rx={1.5}
      style={{
        fill: 'transparent',
        stroke: 'rgb(var(--border))',
        strokeWidth: 0.8,
        strokeDasharray: '3 3',
      }}
    />
  );
}

// ─── Canvas props ─────────────────────────────────────────────────────────────

export interface PanelBoardCanvasProps {
  layout: PanelLayout;
  geometry: EnclosureGeometry;
  // Device ids to render with the relationship highlight (group/line focus).
  highlightedDeviceIds?: ReadonlySet<string>;
  // Short line labels keyed by the MCB protection device id.
  deviceLineLabels?: ReadonlyMap<string, string>;
  // Interaction props — all absent means read-only mode.
  selectedDeviceId?: string | null;
  // True when a placement is in progress (a device is selected for move, or add-mode
  // is active): empty/reserve slots accepted by canPlaceSelectedAt highlight as targets.
  placementActive?: boolean;
  onSelectDevice?: (deviceId: string | null) => void;
  // Opens the device action menu on a settled click (not a drag) at the pointer point.
  onOpenDeviceMenu?: (deviceId: string, at: { x: number; y: number }) => void;
  onSlotClick?: (row: number, col: number) => void;
  canPlaceSelectedAt?: (row: number, col: number) => boolean;
  // True when dropping the selected/dragged device on (row, col) would swap it with
  // the device already there. Drives swap-target highlighting during a drag.
  canSwapSelectedAt?: (row: number, col: number) => boolean;
  // Drag-specific: editor applies moveDevice directly, bypassing selection state closure.
  onMoveDevice?: (deviceId: string, row: number, col: number) => void;
}

// ─── Main canvas component ────────────────────────────────────────────────────

export function PanelBoardCanvas({
  layout,
  geometry,
  highlightedDeviceIds,
  deviceLineLabels,
  selectedDeviceId,
  placementActive,
  onSelectDevice,
  onOpenDeviceMenu,
  onSlotClick,
  canPlaceSelectedAt,
  canSwapSelectedAt,
  onMoveDevice,
}: PanelBoardCanvasProps) {
  const { t } = useTranslation();

  const svgW = geometry.viewW;
  const svgH = geometry.viewH;
  const mw = geometry.modWidth;
  const dh = geometry.deviceHeight;

  const interactive = Boolean(onSelectDevice);

  // ── Drag state ───────────────────────────────────────────────────────────────
  const svgRef = useRef<SVGSVGElement>(null);

  // Drag gesture state in a ref so pointer handlers don't stale-close over it.
  const dragRef = useRef<{
    deviceId: string;
    startX: number;
    startY: number;
    isDragging: boolean;
    wasSelectedBefore: boolean;
    pointerId: number;
    // Mirror of dragTarget state — read in callbacks to avoid dragTarget dep.
    target: { row: number; col: number } | null;
  } | null>(null);

  // State drives re-renders for visual feedback only.
  const [draggingDeviceId, setDraggingDeviceId] = useState<string | null>(null);
  const [dragTarget, setDragTarget] = useState<{ row: number; col: number } | null>(null);

  const handleDevicePointerDown = useCallback((
    e: React.PointerEvent,
    deviceId: string,
    isCurrentlySelected: boolean,
  ) => {
    if (!interactive) return;
    e.preventDefault();
    dragRef.current = {
      deviceId,
      startX: e.clientX,
      startY: e.clientY,
      isDragging: false,
      wasSelectedBefore: isCurrentlySelected,
      pointerId: e.pointerId,
      target: null,
    };
    // Capture so pointer events continue even if cursor leaves the SVG.
    svgRef.current?.setPointerCapture(e.pointerId);
    // Select immediately so valid-slot highlights appear during drag.
    if (!isCurrentlySelected) onSelectDevice?.(deviceId);
  }, [interactive, onSelectDevice]);

  const handleSvgPointerMove = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.isDragging && dx * dx + dy * dy < DRAG_THRESHOLD * DRAG_THRESHOLD) return;

    if (!drag.isDragging) {
      drag.isDragging = true;
      setDraggingDeviceId(drag.deviceId);
    }

    const svg = svgRef.current;
    if (!svg) return;
    const slot = pointToGridSlot(e.clientX, e.clientY, svg, geometry);
    drag.target = slot;   // update ref for use in handleSvgPointerUp
    setDragTarget(slot);  // update state for highlight re-render
  }, [geometry]);

  const handleSvgPointerUp = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;

    svgRef.current?.releasePointerCapture(e.pointerId);

    if (drag.isDragging) {
      // Commit drag: Editor validates via canPlaceDevice and applies moveDevice.
      if (drag.target) onMoveDevice?.(drag.deviceId, drag.target.row, drag.target.col);
      // If move was invalid, Editor's moveDevice returns same ref → no state change.
    } else if (drag.wasSelectedBefore) {
      // Pure click on the already-selected device: toggle it off.
      onSelectDevice?.(null);
    } else {
      // Pure click that selected the device on pointerDown: open its action menu.
      onOpenDeviceMenu?.(drag.deviceId, { x: e.clientX, y: e.clientY });
    }

    dragRef.current = null;
    setDraggingDeviceId(null);
    setDragTarget(null);
  }, [onMoveDevice, onSelectDevice, onOpenDeviceMenu]);

  const handleSvgPointerCancel = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragRef.current = null;
    setDraggingDeviceId(null);
    setDragTarget(null);
  }, []);

  const isDraggingActive = draggingDeviceId !== null;

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg
        ref={svgRef}
        width={svgW * DISPLAY_SCALE}
        height={svgH * DISPLAY_SCALE}
        viewBox={`0 0 ${svgW} ${svgH}`}
        style={{
          display: 'block',
          fontFamily: '"JetBrains Mono", ui-monospace, monospace',
          cursor: isDraggingActive ? 'grabbing' : undefined,
          userSelect: 'none',
          maxWidth: '100%',
          height: 'auto',
        }}
        aria-label={t('panel_board.canvas_aria')}
        onPointerMove={interactive ? handleSvgPointerMove : undefined}
        onPointerUp={interactive ? handleSvgPointerUp : undefined}
        onPointerCancel={interactive ? handleSvgPointerCancel : undefined}
      >
        {/* Enclosure frame: rounded body, rails with mounting holes, optional labels. */}
        <image
          href={geometry.variant.assetUrl}
          x={0} y={0} width={svgW} height={svgH}
          preserveAspectRatio="none"
          style={{ pointerEvents: 'none' }}
        />

        {layout.rows.map((panelRow) => {
          const rowIdx = panelRow.rowIndex;
          if (rowIdx >= geometry.rows) return null; // device row outside the chosen enclosure
          const deviceTop = geometry.deviceTopY(rowIdx);

          return (
            <g key={rowIdx}>
              {panelRow.slots.map((slot) => {
                const colIdx = slot.columnIndex;
                const sx = geometry.slotX(colIdx);
                const isAtDragTarget = dragTarget?.row === rowIdx && dragTarget?.col === colIdx;

                // ── device_anchor ──────────────────────────────────────────
                if (slot.kind === 'device_anchor' && slot.device) {
                  const deviceId = slot.device.deviceId;
                  const isSelected = deviceId === selectedDeviceId;
                  const isHighlighted = Boolean(highlightedDeviceIds?.has(deviceId));
                  const isDragging = deviceId === draggingDeviceId;
                  const spanCols = Math.min(slot.device.modulesWidth, geometry.modulesPerRow - colIdx);
                  // Swap feedback: this device is a valid partner for the dragged one,
                  // and the active target when the drag cursor is over its span.
                  const isSwapCandidate = isDraggingActive
                    && deviceId !== draggingDeviceId
                    && Boolean(canSwapSelectedAt?.(rowIdx, colIdx));
                  const isSwapTarget = isSwapCandidate
                    && dragTarget?.row === rowIdx
                    && dragTarget.col >= colIdx
                    && dragTarget.col < colIdx + spanCols;
                  return (
                    <DeviceRect
                      key={colIdx}
                      device={slot.device}
                      lineLabel={deviceLineLabels?.get(deviceId)}
                      x={sx}
                      y={deviceTop}
                      w={spanCols * mw}
                      h={dh}
                      isSelected={isSelected}
                      isHighlighted={isHighlighted}
                      isDragging={isDragging}
                      isSwapCandidate={isSwapCandidate}
                      isSwapTarget={isSwapTarget}
                      onPointerDown={interactive
                        ? (e) => handleDevicePointerDown(e, deviceId, isSelected)
                        : undefined}
                    />
                  );
                }

                // ── reserve ────────────────────────────────────────────────
                if (slot.kind === 'reserve') {
                  const isValidTarget = Boolean(
                    placementActive && canPlaceSelectedAt?.(rowIdx, colIdx),
                  );
                  const isActiveDragTarget = isDraggingActive && isAtDragTarget && isValidTarget;
                  return (
                    <g
                      key={colIdx}
                      onClick={!isDraggingActive && onSlotClick
                        ? () => onSlotClick(rowIdx, colIdx)
                        : undefined}
                      style={{ cursor: !isDraggingActive && onSlotClick ? 'pointer' : undefined }}
                    >
                      <ReserveRect x={sx} y={deviceTop} w={mw} h={dh} />
                      {isValidTarget && (
                        <rect
                          x={sx + 0.5} y={deviceTop}
                          width={mw - 1} height={dh}
                          rx={1.5}
                          className="no-print"
                          style={{
                            fill: isActiveDragTarget
                              ? 'rgb(var(--success) / 0.45)'
                              : 'rgb(var(--success) / 0.22)',
                            stroke: isActiveDragTarget ? 'rgb(var(--success))' : 'none',
                            strokeWidth: 1.2,
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                    </g>
                  );
                }

                // ── device_continuation ────────────────────────────────────
                if (slot.kind === 'device_continuation') {
                  if (!interactive) return null;
                  const anchorId = findContinuationAnchor(layout, rowIdx, colIdx);
                  if (!anchorId) return null;
                  const isAnchorSelected = anchorId === selectedDeviceId;
                  return (
                    <rect
                      key={colIdx}
                      x={sx} y={deviceTop}
                      width={mw} height={dh}
                      style={{ fill: 'transparent', cursor: 'grab' }}
                      onPointerDown={
                        (e) => handleDevicePointerDown(e, anchorId, isAnchorSelected)
                      }
                    />
                  );
                }

                // ── empty ──────────────────────────────────────────────────
                const isValidTarget = Boolean(
                  selectedDeviceId && canPlaceSelectedAt?.(rowIdx, colIdx),
                );
                const isActiveDragTarget = isDraggingActive && isAtDragTarget && isValidTarget;
                if (!isValidTarget && !onSlotClick) return null;
                return (
                  <rect
                    key={colIdx}
                    x={sx + 0.5} y={deviceTop}
                    width={mw - 1} height={dh}
                    rx={1.5}
                    onClick={!isDraggingActive && onSlotClick
                      ? () => onSlotClick(rowIdx, colIdx)
                      : undefined}
                    className={isValidTarget ? 'no-print' : undefined}
                    style={{
                      fill: isActiveDragTarget
                        ? 'rgb(var(--success) / 0.45)'
                        : isValidTarget
                        ? 'rgb(var(--success) / 0.22)'
                        : 'transparent',
                      stroke: isActiveDragTarget ? 'rgb(var(--success))' : 'none',
                      strokeWidth: 1.2,
                      cursor: !isDraggingActive && onSlotClick ? 'pointer' : 'default',
                    }}
                  />
                );
              })}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
